/**
 * CC-1e — drawing-aid toggle telemetry bridge (ТЗ §14 "clicks" axis).
 *
 * One-way observer: AutoCAD drawing-aid sysvar writes (OSNAP / ORTHO / POLAR /
 * SNAP / GRID) → a typed `ui-toggle` telemetry event on the editor audit ring.
 *
 * Why a bridge (not a per-button handler): these aids have NO dedicated status-
 * bar button component — they are flipped from several places (the F7–F10
 * function keys in `sysvarHotkeys.ts`, `SETVAR`, the future status-bar
 * toggles). `sysvarStore` is the single source of truth for their value, so
 * subscribing to it captures EVERY user toggle path exactly once, with no
 * double-emit and no coupling into each trigger site. Mirrors the existing
 * `installUndolimitSysvarBridge` / `installPreviewSysvarBridge` pattern (state/
 * may import both stores, so the module graph stays acyclic).
 *
 * NOT captured: programmatic DXF-HEADER hydration. A DXF load calls
 * `hydrateFromDxf`, which rewrites ORTHOMODE/POLARMODE/SNAPMODE/GRIDMODE/OSMODE
 * to the file's values — that is NOT a user toggle, so emitting telemetry for
 * it would be a false "user toggled the aid" signal (P2-2). Hydration writes
 * are tagged `lastWriteSource:"hydrate"` and skipped below; genuine F-key /
 * SETVAR / status-bar writes carry "user" and fall through.
 *
 * Observability only: the bridge reads sysvar state and emits a telemetry
 * audit event (ring-only, no command-feed transcript) — it never mutates a
 * sysvar or any geometry/command state.
 *
 * Install once at App boot next to the other sysvar bridges; the returned
 * teardown unsubscribes on unmount to avoid HMR subscriber pile-up.
 */

import type { SysvarKey } from "../dynamicInput/sysvarSchema";
import {
  useSysvarStore,
  POLARMODE_TRACKING_ON_BIT,
} from "../dynamicInput/sysvarStore";
import { OSMODE_SUPPRESS_ALL } from "../dynamicInput/sysvarHotkeys";
import { useEditorStore } from "./editorStore";

/**
 * The drawing aids surfaced as on/off toggles (AutoCAD status-bar parity).
 * OSMODE is a bitmask — "enabled" means "any running object snap active"
 * (value !== 0). OTRACK has no dedicated sysvar in this schema; AutoCAD groups
 * object-snap tracking under POLAR tracking, so POLARMODE covers it.
 */
const TOGGLE_SYSVARS: readonly SysvarKey[] = [
  "ORTHOMODE",
  "POLARMODE",
  "SNAPMODE",
  "GRIDMODE",
  "OSMODE",
] as const;

export function installSysvarToggleTelemetryBridge(): () => void {
  return useSysvarStore.subscribe((next, prev) => {
    // CC-1e P2-2 — only USER-driven toggles are telemetry. Programmatic DXF
    // hydration (`hydrateFromDxf`) tags its write "hydrate"; skip it so a DXF
    // load is never reported as the user toggling ORTHO/POLAR/SNAP/GRID/OSNAP.
    if (next.lastWriteSource === "hydrate") return;
    for (const key of TOGGLE_SYSVARS) {
      const nextValue = next.values[key];
      if (nextValue === prev.values[key]) continue;
      // #349 P2 — OSMODE is a bitmask with a suppress-all sentinel (16384): a
      // value of 16384 (or 16384|modes) keeps the chosen mode bits but turns
      // running snaps OFF (F3-off), so the live resolver treats it as disabled
      // (selectEffectiveOsmode). The toggle telemetry must agree — otherwise a
      // suppressed OSMODE reports enabled:true while snaps are actually off,
      // corrupting the §14 click/toggle analytics for the F3-off case.
      // POLARMODE is a bitcode: "enabled" is the master bit 0
      // (POLARMODE_TRACKING_ON_BIT), NOT `!== 0` — else configuring a config-only
      // bit (POLARADDANG, 4) with the master off would report the aid as toggled
      // ON while polar is actually off, mismatching selectEffectivePolar and
      // corrupting the §14 toggle analytics (#492 @codex P2).
      let enabled: boolean;
      if (key === "OSMODE") {
        enabled = nextValue !== 0 && (nextValue & OSMODE_SUPPRESS_ALL) === 0;
      } else if (key === "POLARMODE") {
        // A config-only POLARMODE edit that does not flip the master bit — e.g.
        // ticking POLARADDANG (bit 4) with the master off, raw value 0 → 4 —
        // changes the value WITHOUT changing the effective on/off state, so it is
        // a configuration edit, not a POLAR toggle. Emit only when the master-bit
        // enabled state actually changes; otherwise §14 toggle analytics would
        // count config edits as POLAR toggles (#492 @codex P2). OSMODE keeps its
        // #349 record-on-any-change contract (a suppressed 0 → 16385 still records
        // enabled:false), so this skip is deliberately POLARMODE-only.
        const nextEnabled = (nextValue & POLARMODE_TRACKING_ON_BIT) !== 0;
        const prevEnabled =
          (prev.values[key] & POLARMODE_TRACKING_ON_BIT) !== 0;
        if (nextEnabled === prevEnabled) continue;
        enabled = nextEnabled;
      } else {
        enabled = nextValue !== 0;
      }
      useEditorStore.getState().recordUiToggle({
        setting: key,
        enabled,
        // Observed state change — the trigger (F-key / SETVAR / status bar) is
        // not knowable here, so the neutral "system" source is used.
        source: "system",
      });
    }
  });
}
